import {
  AssetCategory,
  AssetContainerType,
  AssetCreationReq,
  AssetCreationType,
  AssetFormValue,
  AssetItem,
  AssetsResponse,
  WaiverInfoType,
} from 'src/api/api-types/assets';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { ApiReq, emptyValue } from '../../api';
import { AssetsApi } from 'src/api/assets';
import { FullDescriptionResponse, ItemIdType, ItemType } from '../../types';
import { dateToISO, removeTimezone } from 'src/utils/date';
import { AssetAttachments } from './assets-attachments';
import { LinkedFileResponse, LinkFileDto } from 'src/api/api-types/files';
import { ACCOUNT_TYPE_OBJECT } from '../../utils/constants';
import format from 'date-fns/format';

export class AssetModification {
  public api = new AssetsApi();
  readonly canUpdate?: boolean = true;
  readonly isRelatedEntityActive?: boolean = true;

  @observable id: number;
  @observable updateDescription?: boolean;
  @observable category?: Partial<AssetCategory>;
  @observable subCategory?: Partial<AssetCategory>;
  @observable containerTypes?: AssetContainerType[];
  @observable shortText: string;
  @observable type?: ItemType;
  @observable description: string;
  @observable descriptionTruncated: boolean;
  @observable accountNumber?: ItemType | null;
  @observable relatedLookupsName?: string;
  @observable dateIssued: Date | string | null;
  @observable relatedTo?: ItemType | null;
  @observable relatedToType?: ItemType | null;
  @observable attachments: AssetAttachments;
  @observable waiverInfo?: WaiverInfoType;
  @observable status?: ItemIdType;
  @observable user: AssetItem['user'];
  @observable priority?: ItemIdType;
  @observable assetDate?: Date | string | null;
  @observable waiverInfoStatusId: AssetCreationType['waiverInfoStatusId'];
  @observable waiverInfoFollowUpDate?: WaiverInfoType['followUpDate'];

  @observable dueDate?: Date | string | null;

  @observable.ref createReq: ApiReq<AssetItem> = emptyValue;
  @observable.ref editReq: ApiReq<AssetItem> = emptyValue;
  @observable.ref deleteReq: ApiReq<AssetsResponse> = emptyValue;
  @observable.ref fullDescriptionReq: ApiReq<FullDescriptionResponse> =
    emptyValue;
  @observable createFormData?: AssetCreationReq;
  @observable.ref linkFileReq: ApiReq<LinkedFileResponse> = emptyValue;

  constructor(asset: AssetCreationType) {
    makeObservable(this);

    const {
      id,
      type,
      category,
      subCategory,
      containerTypes,
      shortText,
      description,
      relatedTo,
      account,
      relatedToType,
      totalAttachments,
      dateIssued,
      descriptionTruncated,
      canUpdate,
      isRelatedEntityActive,
      waiverInfo,
      waiverInfoStatusId,
      waiverInfoFollowUpDate,
      status,
      user,
      priority,
      assetDate,
      dueDate,
    } = asset;

    this.id = id;
    this.type = type;
    this.category = category;
    this.subCategory = subCategory;
    this.containerTypes = containerTypes || [];
    this.shortText = shortText || '';
    this.description = description || '';
    this.descriptionTruncated = descriptionTruncated || false;
    this.accountNumber = account || null;
    this.dateIssued = dateIssued || null;
    this.attachments = new AssetAttachments({
      id,
      totalAttachments: totalAttachments,
    });
    this.relatedTo = relatedTo || null;
    this.relatedToType = relatedToType || null;
    this.canUpdate = canUpdate;
    this.isRelatedEntityActive = isRelatedEntityActive;
    this.waiverInfo = waiverInfo;
    this.waiverInfoStatusId =
      waiverInfo === null
        ? null
        : waiverInfoStatusId
        ? waiverInfoStatusId
        : null;
    this.waiverInfoFollowUpDate = waiverInfoFollowUpDate;
    this.status = status;
    this.user = user ? { id: user.id, name: user.name || '' } : this.user;
    this.priority = priority;
    this.assetDate = assetDate || null;
    this.dueDate = dueDate || null;
  }

  @computed get serialized() {
    return {
      id: this.id,
      shortText: this.shortText,
      type: this.type?.id ? { id: this.type?.id } : null,
      category: this.category?.id ? { id: this.category?.id } : null,
      description: this.description,
      accountNumber: this.accountNumber?.id
        ? { id: this.accountNumber?.id }
        : null,
      dateIssued: this.dateIssued,
      relatedTo: this.relatedTo?.id ? this.relatedTo : null,
      relatedToType: this.relatedToType?.id ? this.relatedToType : null,
      subCategory: !this.subCategory
        ? null
        : typeof this.subCategory === 'string'
        ? { id: this.subCategory }
        : this.subCategory,
      containerTypes: this.containerTypes || [],
      updateDescription: this.updateDescription,
      status: this.status && { id: this.status.id },
      user: this.user ? { id: this.user.id } : this.user,
      priority: this.priority && { id: this.priority.id },
      assetDate: removeTimezone(this.assetDate) ?? null,
      dueDate: removeTimezone(this.dueDate) ?? null,
      waiverInfo:
        this.waiverInfo === null
          ? this.waiverInfo
          : this.waiverInfoStatusId
          ? {
              status: this.waiverInfoStatusId
                ? { id: Number(this.waiverInfoStatusId) }
                : undefined,
              followUpDate: this.waiverInfoFollowUpDate,
            }
          : null,
    };
  }

  @computed get fullDescription() {
    if (
      this.fullDescriptionReq.state !== 'fulfilled' ||
      !this.fullDescriptionReq.value
    ) {
      return '';
    }

    return this.fullDescriptionReq.value.data?.description || '';
  }

  @action setEntity = async (assetsFields: Partial<AssetCreationType>) => {
    this.shortText =
      !!assetsFields.shortText || assetsFields.shortText === ''
        ? assetsFields.shortText
        : this.shortText;
    this.description =
      !!assetsFields.description || assetsFields.description === ''
        ? assetsFields.description
        : this.description;
    this.type = !!assetsFields.type ? assetsFields.type : this.type;
    this.relatedTo = !!assetsFields.account
      ? assetsFields.account
      : !!assetsFields.relatedTo
      ? assetsFields.relatedTo
      : this.relatedTo;
    this.relatedToType = !!assetsFields.account
      ? ACCOUNT_TYPE_OBJECT
      : !!assetsFields.relatedToType
      ? assetsFields.relatedToType
      : this.relatedToType;
    this.accountNumber = !!assetsFields.account
      ? assetsFields.account
      : this.accountNumber;
    this.dateIssued = !!assetsFields.dateIssued
      ? assetsFields.dateIssued
      : null;
    this.updateDescription = !!assetsFields.updateDescription
      ? assetsFields.updateDescription
      : false;
    this.waiverInfoFollowUpDate = assetsFields.waiverInfoFollowUpDate;
    this.waiverInfoStatusId = assetsFields.waiverInfoStatusId;
    this.containerTypes = assetsFields.containerTypes
      ? assetsFields.containerTypes
      : this.containerTypes;
    this.accountNumber = assetsFields.account
      ? assetsFields.account
      : this.accountNumber;

    this.waiverInfo = !!assetsFields.waiverInfoStatusId
      ? {
          status: {
            id: Number(assetsFields.waiverInfoStatusId) ?? null,
          },
          followUpDate: assetsFields.waiverInfoFollowUpDate
            ? dateToISO(assetsFields.waiverInfoFollowUpDate)
            : null,
        }
      : this.waiverInfo;
    this.status = assetsFields.status;
    this.user = assetsFields.user;
    this.priority = assetsFields.priority;
    this.category = assetsFields.category
      ? { id: Number(assetsFields?.category) || assetsFields?.category?.id }
      : this.category;
      this.subCategory = !!assetsFields.subCategory ? assetsFields.subCategory : this.subCategory;
    this.containerTypes = assetsFields.containerTypes
      ? assetsFields.containerTypes
      : this.containerTypes;
    this.assetDate = !assetsFields.assetDate
      ? null
      : format(new Date(assetsFields.assetDate), 'yyyy-MM-dd');
    this.dueDate = !assetsFields.dueDate
      ? null
      : format(new Date(assetsFields.dueDate), 'yyyy-MM-dd');
  };

  @action setCreationForm = (formData: AssetFormValue) => {
    this.createFormData = {
      shortText: formData.shortText,
      description: formData.description,
      accountNumber: formData.accountNumber
        ? { id: Number(formData.accountNumber?.id) }
        : null,
      dateIssued: formData.dateIssued ? dateToISO(formData.dateIssued) : null,
      type: { id: Number(formData.type) || formData?.type?.id },
      category: { id: Number(formData?.category) || formData?.category?.id },
      relatedTo: formData.relatedTo
        ? { id: Number(formData.relatedTo?.id) }
        : null,
      relatedToType: formData.relatedToType
        ? { id: Number(formData.relatedToType?.id) }
        : null,
      ...(formData?.status && { status: formData.status }),
      ...(formData?.user && { user: formData.user }),
      ...(formData?.priority && { priority: formData.priority }),
      ...(formData?.assetDate && {
        assetDate: formData.assetDate
          ? format(new Date(formData.assetDate), 'yyyy-MM-dd')
          : null,
      }),
      ...(formData?.dueDate && {
        dueDate: formData.dueDate
          ? format(new Date(formData.dueDate), 'yyyy-MM-dd')
          : null,
      }),
      waiverInfo: formData.waiverInfoStatusId
        ? {
            status: {
              id: Number(formData.waiverInfoStatusId) ?? null,
            },
            followUpDate: formData.waiverInfoFollowUpDate
              ? dateToISO(formData.waiverInfoFollowUpDate)
              : null,
          }
        : null,
      subCategory: formData.subCategory
        ? { id: Number(formData.subCategory) || formData.subCategory.id }
        : null,
      containerTypes: formData.containerTypes ? formData.containerTypes : null,
    };
  };

  @action createAsset = async (data?: AssetCreationReq) => {
    const formData = this.createFormData ? this.createFormData : data;
    if (!formData) return;
    this.createReq = this.api.createAsset(formData);
    this.createReq.then(res => (this.id = res?.data?.id ?? 0));
    return this.createReq;
  };

  @action createIRAsset = async () => {
    if (!this.createFormData) return;
    this.createReq = this.api.createIRAsset(this.createFormData);
    this.createReq.then(res => (this.id = res?.data?.id ?? 0));
    return this.createReq;
  };

  @action editAsset = async () => {
    if (!this.id) return null;
    this.editReq = this.api.updateAsset({
      ...this.serialized,
      dateIssued: this.serialized.dateIssued
        ? format(new Date(this.serialized.dateIssued), 'yyyy-MM-dd')
        : this.serialized.dateIssued,
    });
    return this.editReq;
  };

  @action getFullDescription = async () => {
    if (this.descriptionTruncated) {
      this.fullDescriptionReq = this.api.getFullDescription(this.id);
      await this.fullDescriptionReq;
      runInAction(() => {
        this.descriptionTruncated = false;
        this.description = this.fullDescription;
      });
    }
  };

  @action linkFile = async (file: LinkFileDto) => {
    this.linkFileReq = this.api.linkFileToAsset(this.id, file);
    await this.linkFileReq;
    return this.linkFileReq;
  };

  @action deleteAsset = async () => {
    if (!this.id) return null;
    this.deleteReq = this.api.deleteAsset(this.id);
    return this.deleteReq;
  };

  @action reset = (data: AssetModification) => {
    this.type = data.type;
    this.category = data.category;
    this.subCategory = data.subCategory;
    this.relatedTo = data.relatedTo;
    this.relatedToType = data.relatedToType;
  };
}
